import type { SelectOption } from '../components/CustomSelect'

/**
 * 웨이팅 시간 선택지.
 *
 * 예전에는 30분 단위로 12시간까지 있었다. 24개가 넘는 목록을 굴려야 했고,
 * 실제로 저장된 값은 30·60·210분뿐이었다. 식당 웨이팅에서 3시간을 넘기는 일은
 * 드물고, 그 이상은 정확한 수치보다 "오래 기다렸다"는 사실이 중요하다.
 *
 * 그래서 15분 단위로 촘촘하게 하되 2시간에서 끊고, 그 위는 하나로 묶는다.
 *
 * 이 파일에 모아둔 이유
 * -------------------
 * 같은 생성 로직이 VisitDateAdd의 옵션 목록·초기값, StoreReview의 편집 복원
 * 세 곳에 복사돼 있었다. 단위를 바꾸려면 세 군데를 똑같이 고쳐야 했다.
 */

/** 선택지 최소 단위(분) */
export const WAITING_TIME_STEP = 15

/** 개별 선택지로 제공하는 최대치(분) */
export const WAITING_TIME_MAX = 120

/**
 * '2시간 이상'을 나타내는 값(분).
 *
 * 서버는 이 값을 분 단위 정수로 그대로 저장한다. 121은 "2시간을 넘겼다"는
 * 뜻이지 정확히 121분이라는 뜻이 아니다. 경계값(120)과 구분되기만 하면 된다.
 */
export const WAITING_TIME_OVER = 121

/** '없음' 선택지의 값 */
export const WAITING_TIME_NONE = 'none'

/** 분 → 사람이 읽는 라벨 ('1시간 30분') */
export function formatWaitingTime(minutes: number): string {
  if (minutes > WAITING_TIME_MAX) {
    return '2시간 이상'
  }

  if (minutes < 60) {
    return `${minutes}분`
  }

  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60

  return rest === 0 ? `${hours}시간` : `${hours}시간 ${rest}분`
}

/**
 * 선택지 전체 목록.
 *
 * 없음 · 15분 · 30분 · 45분 · 1시간 · … · 2시간 · 2시간 이상
 */
export function buildWaitingTimeOptions(): SelectOption[] {
  const options: SelectOption[] = [
    { value: WAITING_TIME_NONE, label: '없음' },
  ]

  for (
    let minutes = WAITING_TIME_STEP;
    minutes <= WAITING_TIME_MAX;
    minutes += WAITING_TIME_STEP
  ) {
    options.push({
      value: minutes.toString(),
      label: formatWaitingTime(minutes),
    })
  }

  options.push({
    value: WAITING_TIME_OVER.toString(),
    label: '2시간 이상',
  })

  return options
}

/**
 * 저장된 분 값을 선택지로 되돌린다.
 *
 * 예전 단위로 저장된 값(210분 등)은 목록에 없다. 그런 값은 '2시간 이상'으로
 * 묶어 보여준다. 편집 화면에서 빈칸이 되거나 목록에 없는 항목이 뜨지 않도록.
 */
export function toWaitingTimeOption(minutes: number | null): SelectOption | null {
  if (minutes === null) {
    return null
  }

  if (minutes > WAITING_TIME_MAX) {
    return { value: WAITING_TIME_OVER.toString(), label: '2시간 이상' }
  }

  return { value: minutes.toString(), label: formatWaitingTime(minutes) }
}
